import type { ProteinCalcProduct } from '@bulknutrients/bulk-shared-lib/interfaces/ProteinCalculator';
import {
  CLOUDINARY_ASSET_FIELDS,
  IMAGE_FIELDS,
} from '@bulknutrients/bulk-shared-lib/lib/queries/sanity/snippets';
import { client } from '@bulknutrients/bulk-shared-lib/lib/sanity/client';
import { Container } from '@components/Container';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { StandardLayout } from '@components/Layouts/StandardLayout';
import { PortableTextBlockRenderer } from '@components/PortableText/PortableText';
import { ProteinCalculator } from '@components/ProteinCalculator';
import { ProteinCalculatorSkeleton } from '@components/ProteinCalculator/ProteinCalculator';
import type { PortableTextBlock } from '@portabletext/react';
import Head from 'next/head';
import type { ReactElement, ReactNode } from 'react';
import useSWR from 'swr';

const RendererOverride = {
  block: {
    h2: ({ children }: { children?: ReactNode }) => (
      <>
        <HorizontalDivider />
        <h2 className="italic has-[~figure]:inline-block has-[~figure]:w-fit">
          {children}
        </h2>
      </>
    ),
    h3: ({ children }: { children?: ReactNode }) => (
      <>
        <HorizontalDivider className="via-grey-mid" />
        <h3 className="font-bold">{children}</h3>
      </>
    ),
  },
};

const fetcher = async () => {
  const query = `//groq
    *[_type == "proteinCalculator"]{
      preContent,
      postContent,
      "competitorBrands": competitorBrands[]->{
        brand,
        products
      },
      "products": products[]->{
        "description": shortDescription,
        sku,
        "name": coalesce(shortTitle, title),
        "image": images[defaultImage == true][0]{
            _type == "image" => @{
              ${IMAGE_FIELDS}
            },
            _type == "cloudinaryAsset" => @{
              ${CLOUDINARY_ASSET_FIELDS}
          },
        },
        "displayWeight": defaultVariant->options[title=="Package Size"][0].value,
        price,
        "protein": description[contentDescription == "Nutrition Panels"][0].contentBody[1].content.rows[2].cells[2],
        "slug": slug.current     
      }
    }
  `;

  return (await client.fetch(query))[0];
};

interface IProteinCalculator {
  preContent: PortableTextBlock[];
  postContent: PortableTextBlock[];
  products: ProteinCalcProduct[];
  competitorBrands: { brand: string; products: ProteinCalcProduct[] }[];
}
export default function ProteinCalculatorPage() {
  const { data, isLoading } = useSWR<IProteinCalculator>(
    'protein-calculator',
    fetcher
  );

  const { preContent, postContent, competitorBrands, products } = data || {};

  return (
    <>
      <Head>
        <title>
          Compare Your Protein Price & Purity To Ours! Bulk Nutrients
        </title>
        <meta
          name="title"
          content="Compare Your Protein Price & Purity To Ours! Bulk Nutrients"
        />
        <meta
          name="description"
          content="See how much you can save on supplements by shopping online at Bulk Nutrients! Compare to your current brand and get the best bang for your buck."
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/protein-calculator`}
        />
      </Head>
      <Container as="main" id="main-content">
        <h1>Online Protein Calculator</h1>
        {isLoading || !data ? (
          <ProteinCalculatorSkeleton />
        ) : (
          <>
            <PortableTextBlockRenderer
              content={preContent ?? []}
              componentsOverride={RendererOverride}
            />
            <ProteinCalculator
              products={products ?? []}
              competitorBrands={competitorBrands ?? []}
            />
            <PortableTextBlockRenderer
              content={postContent ?? []}
              componentsOverride={RendererOverride}
            />
          </>
        )}
      </Container>
    </>
  );
}

ProteinCalculatorPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};
